/**
 * useVoiceOutput — TTS via Web Speech Synthesis API.
 *
 * Funciona em Web + WKWebView (iOS) + Android WebView.
 *
 * PROBLEMAS CONHECIDOS iOS WKWebView (HLOS-321):
 * - speechSynthesis.getVoices() retorna array VAZIO inicialmente.
 *   Vozes carregam assincronamente via evento 'voiceschanged'.
 *   Se speak() for chamado antes das vozes carregarem, o utterance
 *   pode tocar com voz default (geralmente inglês) ou silenciar.
 * - utterance.volume default pode ser 0 em alguns builds; setar 1.0
 *   explicitamente é defensivo.
 * - iOS pode cancelar fala após 10s de inatividade do app. Cancel
 *   preemptivo entre falas longas ajuda.
 *
 * Constitucional:
 * - CL-08: opt-in; só fala se `voiceOutputEnabled=true`.
 * - P-UX-10 (Quiet as feature): nunca fala automaticamente;
 *   apenas quando o aprendiz pede.
 * - Princípio XI: pronúncia neutra; sem efeito "robótico" forçado.
 * - P-UX-08: feedback honesto — se não tocar, diz por quê.
 */

import { ref, onBeforeUnmount } from "vue";
import { appleTTSBridge } from "../../capacitor/apple_tts_bridge";

interface SpeakOptions {
  rate?: number; // 0.5..2.0
  pitch?: number; // 0.5..2.0
  voiceURI?: string;
  lang?: string;
  volume?: number; // 0.0..1.0 (default 1.0)
}

export function useVoiceOutput() {
  const isSpeaking = ref(false);
  /** HLOS-321: erro explícito para diagnóstico honesto (P-UX-08). */
  const error = ref<string | null>(null);
  /** HLOS-321: vozes carregadas? Em iOS, pode demorar. */
  const voicesReady = ref(false);
  /** HLOS-321: vozes disponíveis no momento. */
  const voices = ref<SpeechSynthesisVoice[]>([]);

  /** HLOS-323: usa Apple TTS nativo no iOS (confiável), senão Web Speech. */
  const isSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const isNativeTTS = appleTTSBridge.isAvailable();

  function refreshVoices(): void {
    if (!isSupported) return;
    const v = window.speechSynthesis.getVoices();
    voices.value = v;
    voicesReady.value = v.length > 0;
  }

  function pickBestVoice(lang: string, voiceURI?: string): SpeechSynthesisVoice | null {
    const all = voices.value;
    if (all.length === 0) return null;
    // 1. voiceURI explícito
    if (voiceURI) {
      const exact = all.find((v) => v.voiceURI === voiceURI);
      if (exact) return exact;
    }
    // 2. match exato por lang (pt-BR)
    const langLower = lang.toLowerCase();
    const exactLang = all.find((v) => v.lang?.toLowerCase() === langLower);
    if (exactLang) return exactLang;
    // 3. match por prefixo (pt)
    const langPrefix = langLower.split("-")[0] ?? "";
    const prefixMatch = all.find(
      (v) => v.lang && v.lang.toLowerCase().startsWith(langPrefix),
    );
    if (prefixMatch) return prefixMatch;
    // 4. qualquer voz com "default: true"
    const def = all.find((v) => v.default);
    if (def) return def;
    // 5. primeira voz
    return all[0] ?? null;
  }

  function cancel() {
    if (isNativeTTS) {
      void appleTTSBridge.stop();
      isSpeaking.value = false;
      return;
    }
    if (!isSupported) return;
    try {
      window.speechSynthesis.cancel();
    } catch {
      // ignore
    }
    isSpeaking.value = false;
  }

  /**
   * speak — sintetiza o texto. Resolve voz de forma robusta:
   * 1. Se a voz pedida está carregada, usa ela.
   * 2. Caso contrário, espera até 2s pelas vozes carregarem (iOS WKWebView).
   * 3. Se mesmo assim não houver, faz fallback para qualquer voz default.
   * 4. Se não houver voz nenhuma, marca erro (P-UX-08 honestidade).
   */
  function speak(text: string, opts: SpeakOptions = {}): void {
    if (!text || !text.trim()) return;
    error.value = null;
    cancel();

    // HLOS-323: TTS nativo iOS (AVSpeechSynthesizer) tem prioridade.
    // Normaliza rate de Web Speech (0.5..2.0) para Apple TTS (0.0..1.0).
    if (isNativeTTS) {
      const wsRate = opts.rate ?? 1.0;
      const appleRate = Math.max(0, Math.min(1, (wsRate - 0.5) / 1.5));
      appleTTSBridge
        .speak({
          text,
          lang: opts.lang ?? "pt-BR",
          rate: appleRate,
          pitch: opts.pitch ?? 1.0,
        })
        .then(() => {
          isSpeaking.value = true;
          error.value = null;
        })
        .catch((e) => {
          isSpeaking.value = false;
          error.value = e instanceof Error ? e.message : "tts-failed";
        });
      return;
    }

    if (!isSupported) {
      error.value = "unsupported";
      return;
    }

    const lang = opts.lang ?? "pt-BR";

    const doSpeak = () => {
      refreshVoices();
      const voice = pickBestVoice(lang, opts.voiceURI);
      if (!voice) {
        error.value = "no-voice";
        isSpeaking.value = false;
        return;
      }
      const u = new SpeechSynthesisUtterance(text);
      u.rate = opts.rate ?? 1.0;
      u.pitch = opts.pitch ?? 1.0;
      u.volume = opts.volume ?? 1.0;
      u.lang = voice.lang ?? lang;
      u.voice = voice;
      u.onstart = () => {
        isSpeaking.value = true;
        error.value = null;
      };
      u.onend = () => {
        isSpeaking.value = false;
      };
      u.onerror = (e: SpeechSynthesisErrorEvent) => {
        isSpeaking.value = false;
        // Não expor "interrupted"/"canceled" como erro visível
        if (e.error && e.error !== "interrupted" && e.error !== "canceled") {
          error.value = e.error;
        }
      };
      try {
        window.speechSynthesis.speak(u);
        isSpeaking.value = true;
      } catch (e) {
        error.value = e instanceof Error ? e.message : "speak-failed";
        isSpeaking.value = false;
      }
    };

    if (voicesReady.value) {
      doSpeak();
    } else {
      // HLOS-321: iOS WKWebView carrega vozes async. Aguarda até 2s
      // antes de fallback para 'qualquer default'.
      let waited = 0;
      const tick = () => {
        if (voicesReady.value) {
          doSpeak();
          return;
        }
        waited += 100;
        if (waited >= 2000) {
          doSpeak(); // doSpeak() vai cair no fallback 'qualquer voz'
        } else {
          setTimeout(tick, 100);
        }
      };
      // Tenta carregar imediatamente
      refreshVoices();
      if (voicesReady.value) {
        doSpeak();
      } else {
        setTimeout(tick, 100);
      }
    }
  }

  function listVoices(): SpeechSynthesisVoice[] {
    refreshVoices();
    return voices.value;
  }

  // HLOS-321: iOS dispara 'voiceschanged' quando a lista de vozes está
  // disponível. Inicializa listeners.
  if (isSupported && typeof window !== "undefined") {
    refreshVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => refreshVoices();
    }
  }

  onBeforeUnmount(() => {
    cancel();
  });

  return { isSupported, isSpeaking, error, voicesReady, speak, cancel, listVoices };
}
